import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import {
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { Router } from '@angular/router';
import { DEFAULT_DATE_FORMAT } from '@app/helpers/date.helper';
import { CertificateService } from '@app/services/certificates/certificate.service';
import { MultiCertService } from '@app/services/certificates/multi-cert.service';
import { ConfigService } from '@app/services/config.service';
import { UserService } from '@app/services/user.service';
import { CertificateList } from '@app/store/models/certificate.model';
import {
  DropdownItem,
  ISortBy,
  TableColumns,
  TableItems,
} from '@infocert/ng-ui-kit/models';
import { TranslateService } from '@ngx-translate/core';
import { chunk } from 'lodash-es';
import moment from 'moment';
import { CookieService } from 'ngx-cookie-service';
import { LocalStorageService } from 'ngx-webstorage';
import { lastValueFrom } from 'rxjs';

@Component({
  selector: 'app-multi-cert',
  templateUrl: './multi-cert.component.html',
  styleUrls: ['./multi-cert.component.scss'],
  standalone: false,
})
export class MultiCertComponent implements OnInit {
  constructor(
    private certificateService: CertificateService,
    private changeDetectorRef: ChangeDetectorRef,
    private multiCertService: MultiCertService,
    private userService: UserService,
    private cookieService: CookieService,
    private router: Router,
    private localStorageService: LocalStorageService,
    private configService: ConfigService,
    private translateService: TranslateService
  ) {}

  public searchString: string = '';
  public listOrder: string = 'MOST_RECENT';

  // Proprietà per ng-ui-kit-dropdown
  isDropdownOpen: boolean = false;
  orderOptions: DropdownItem[] = [];

  get templateList(): string[] {
    return this?.configService?.config?.templateList;
  }

  get subCAPeruTemplateList(): string[] {
    return this?.configService?.config?.subCAPeruTemplateList;
  }

  loading = false;

  // ng-ui-kit-table properties
  tableColumns: TableColumns[] = [];

  tableItems: TableItems[] = [];
  itemsPerPage: number = 10;
  currentPage: number = 1;
  showDropdown = 'none';
  showFilter = false;

  // Metodo per ng-ui-kit-dropdown
  toggleDropdown(): void {
    this.isDropdownOpen = !this.isDropdownOpen;
  }

  // Metodo per gestire la selezione dal dropdown ng-ui-kit
  onSelectOrder(value: string): void {
    console.log('🚀 ~ MultiCertComponent ~ onSelectOrder ~ value:', value);
    this.listOrder = value;
    this.orderTable(value);

    // Aggiorna le opzioni selezionate
    this.orderOptions = this.orderOptions.map(option => ({
      ...option,
      selected: option.value === value,
    }));

    this.updateTableItems();
  }

  // Manteniamo anche il metodo legacy per compatibilità
  toggleDropdownLegacy() {
    switch (this.showDropdown) {
      case 'none':
        this.showDropdown = 'flex';
        break;
      case 'flex':
        this.showDropdown = 'none';
        break;
    }
  }

  toggleShowFilter() {
    switch (this.showFilter) {
      case false:
        this.showFilter = true;
        break;
      case true:
        this.showFilter = false;
        break;
    }
  }

  get selected() {
    return this.listOrder;
  }

  orderTable(mode: string) {
    console.log('🚀 ~ MultiCertComponent ~ orderTable ~ mode:', mode);
    this.listOrder = mode;

    switch (mode) {
      case 'MOST_RECENT':
        this.filteredCertificateList.sort((a, b) => {
          return this.getIssuanceDateValue(b.issuanceDate) - this.getIssuanceDateValue(a.issuanceDate);
        });
        break;
      case 'LESS_RECENT':
        this.filteredCertificateList.sort((a, b) => {
          return this.getIssuanceDateValue(a.issuanceDate) - this.getIssuanceDateValue(b.issuanceDate);
        });
        break;
      case 'USERNAME_A_Z':
        this.filteredCertificateList.sort((a, b) =>
          a.subjectName.localeCompare(b.subjectName)
        );
        break;
      case 'USERNAME_Z_A':
        this.filteredCertificateList.sort((a, b) =>
          b.subjectName.localeCompare(a.subjectName)
        );
        break;
    }

    this.changeDetectorRef.detectChanges();
  }

  _certificateList: CertificateList[] = [];
  searchedCertificateList: CertificateList[] = [];
  filteredCertificateList: CertificateList[] = [];

  checkActive: boolean = true;
  checkRevoked: boolean = true;
  checkExpire: boolean = true;

  startDate = new UntypedFormControl('', [Validators.required]);
  endDate = new UntypedFormControl('', [Validators.required]);

  form = new UntypedFormGroup({
    startDate: this.startDate,
    endDate: this.endDate,
  });

  // convenience getter for easy access to form fields
  get f() {
    return this.form.controls;
  }

  get formErrors() {
    const errors = this.form.errors;
    return errors;
  }

  submitForm() {
    this.loading = true;
    // stop here if form is invalid
    if (this.form.invalid) {
      return;
    }

    this.filterDate(this.startDate.value, this.endDate.value);

    this.loading = false;
  }

  get checkAllStatus(): boolean {
    return this.checkActive && this.checkRevoked && this.checkExpire;
  }

  get certificateList(): CertificateList[] {
    return this._certificateList;
  }

  get paginatedCertificateList(): CertificateList[] {
    return (
      chunk(this.filteredCertificateList || [], this.itemsPerPage)[
        this.currentPage - 1
      ] || []
    );
  }

  get currentStart(): number {
    if (this.filteredCertificateList.length <= 0) {
      return 0;
    } else {
      return (this.currentPage - 1) * this.itemsPerPage + 1;
    }
  }

  get currentEnd(): number {
    return Math.min(this.currentStart + this.itemsPerPage - 1, this.totalItems);
  }

  get totalItems(): number {
    return this.filteredCertificateList.length || 0;
  }

  // Gestisce il click sulla riga della tabella
  onTableRowClick(item: TableItems): void {
    const cert = this.paginatedCertificateList.find(c => c.uap === item.id);
    if (cert) {
      this.login(cert.uap, cert.profileName, cert.status);
    }
  }

  // Converte i certificati nel formato TableItems per ng-ui-kit-table
  updateTableItems(): void {
    this.tableItems = this.paginatedCertificateList.map(cert => ({
      id: cert.uap,
      values: [
        {
          id: `${cert.uap}.subjectName`,
          field: 'subjectName',
          value: cert.subjectName,
          detail: this.removeTINIT(cert.CF),
        },
        {
          id: `${cert.uap}.profileName`,
          field: 'profileName',
          value: this.getProfileNameDisplay(cert.profileName),
        },
        {
          id: `${cert.uap}.dnq`,
          field: 'dnq',
          value: cert.dnq,
        },
        {
          id: `${cert.uap}.status`,
          field: 'status',
          value: this.getStatusText(cert.status),
          status: this.getStatusIcon(cert.status),
        },
        {
          id: `${cert.uap}.issuanceDate`,
          field: 'issuanceDate',
          value: this.formatDate(cert.issuanceDate),
        },
      ],
    }));
  }

  getProfileNameDisplay(profileName: string): string {
    if (!profileName?.trim()) {
      return this.translateService.instant('UNKNOWN');
    }

    if (this.templateList?.includes(profileName)) {
      return profileName;
    }

    const certTypeKey = `COMMON.${profileName}`;
    const certTypeLabel = this.translateService.instant(certTypeKey);
    if (certTypeLabel !== certTypeKey) {
      return certTypeLabel;
    }

    return profileName;
  }

  // Helper per ottenere il testo dello status
  getStatusText(status: string): string {
    const statusMap: { [key: string]: string } = {
      active: 'MULTI_CERT_PAGE.STATUS.ACTIVE',
      expired: 'MULTI_CERT_PAGE.STATUS.EXPIRED',
      expiring: 'MULTI_CERT_PAGE.STATUS.EXPIRING',
      to_activate: 'MULTI_CERT_PAGE.STATUS.TO_ACTIVATE',
      revoked: 'MULTI_CERT_PAGE.STATUS.REVOKED',
      suspended: 'MULTI_CERT_PAGE.STATUS.SUSPENDED',
    };
    const translationKey = statusMap[status] || 'UNKNOWN';
    return this.translateService.instant(translationKey);
  }

  // Helper per convertire lo status in IconStatus per ng-ui-kit
  getStatusIcon(status: string): any {
    const statusMap: { [key: string]: string } = {
      active: 'success',
      expired: 'error',
      expiring: 'warning',
      to_activate: 'warning',
      revoked: 'error',
      suspended: 'error',
    };
    return statusMap[status] || 'error';
  }

  get minDate(): string {
    return moment(this.startDate.value).format(DEFAULT_DATE_FORMAT);
  }

  // Inizializza le colonne della tabella con le traduzioni
  initializeTableColumns(): void {
    this.tableColumns = [
      {
        field: 'subjectName',
        label: this.translateService.instant(
          'MULTI_CERT_PAGE.TABLE.SUBJECT_NAME'
        ),
        sortable: true,
        detail: true, // Per mostrare il CF come dettaglio
      },
      {
        field: 'profileName',
        label: this.translateService.instant(
          'MULTI_CERT_PAGE.TABLE.PROFILE_NAME'
        ),
        sortable: false,
      },
      {
        field: 'dnq',
        label: this.translateService.instant('MULTI_CERT_PAGE.TABLE.CERT_ID'),
        sortable: false,
      },
      {
        field: 'status',
        label: this.translateService.instant('MULTI_CERT_PAGE.STATUS.TITLE'),
        status: true, // Colonna dedicata per status
        sortable: false,
      },
      {
        field: 'issuanceDate',
        label: this.translateService.instant(
          'MULTI_CERT_PAGE.TABLE.ISSUANCE_DATE'
        ),
        sortable: true,
      },
    ];
  }

  // Inizializza le opzioni del dropdown di ordinamento
  initializeOrderOptions(): void {
    this.orderOptions = [
      {
        label: this.translateService.instant(
          'MULTI_CERT_PAGE.MOST_RECENT_ISSUED'
        ),
        value: 'MOST_RECENT',
        selected: this.listOrder === 'MOST_RECENT',
      },
      {
        label: this.translateService.instant(
          'MULTI_CERT_PAGE.LESS_RECENT_ISSUED'
        ),
        value: 'LESS_RECENT',
        selected: this.listOrder === 'LESS_RECENT',
      },
      {
        label: this.translateService.instant('MULTI_CERT_PAGE.USERNAME_A_Z'),
        value: 'USERNAME_A_Z',
        selected: this.listOrder === 'USERNAME_A_Z',
      },
      {
        label: this.translateService.instant('MULTI_CERT_PAGE.USERNAME_Z_A'),
        value: 'USERNAME_Z_A',
        selected: this.listOrder === 'USERNAME_Z_A',
      },
    ];
  }

  ngOnInit(): void {
    this.initializeTableColumns();
    this.initializeOrderOptions();
    this.getCertificateList();

    this.form.valueChanges.subscribe(() => {
      const allInputsFilled = Object.values(this.form.controls).every(
        control => control.value.trim() !== ''
      );
      if (allInputsFilled) {
        this.submitForm();
      }
    });

    this.localStorageService.clear('isSubCAPeru');
  }

  async getCertificateList() {
    this.loading = true;

    this._certificateList = await lastValueFrom(
      this.certificateService.getMultiCertificateListWithFallback()
    );

    this.searchedCertificateList = this._certificateList;
    this.filteredCertificateList = this.searchedCertificateList;

    this.orderTable('MOST_RECENT');
    this.updateTableItems();

    this.loading = false;
  }

  onSearch() {
    this.searchedCertificateList = this._certificateList.filter(data =>
      this.matchValue(data, this.searchString)
    );

    this.filteredCertificateList = this.searchedCertificateList;

    this.checkActive = true;
    this.checkExpire = true;
    this.checkRevoked = true;

    this.startDate.setValue('');
    this.endDate.setValue('');
    this.orderTable('MOST_RECENT');

    this.currentPage = 1;
  }

  onFilterChange(event: any) {
    const checkbox = event.target;

    if (checkbox.checked) {
      switch (checkbox.value) {
        case 'all':
          this.checkActive = true;
          this.checkRevoked = true;
          this.checkExpire = true;
          break;
        case 'active':
          this.checkActive = true;
          break;
        case 'revoked':
          this.checkRevoked = true;
          break;
        case 'expire':
          this.checkExpire = true;
          break;
      }
    } else {
      switch (checkbox.value) {
        case 'all':
          this.checkActive = false;
          this.checkRevoked = false;
          this.checkExpire = false;
          break;
        case 'active':
          this.checkActive = false;
          break;
        case 'revoked':
          this.checkRevoked = false;
          break;
        case 'expire':
          this.checkExpire = false;
          break;
      }
    }

    // map boolean as string to enable list reading from switch case
    const mappedFilters = [
      this.checkActive,
      this.checkRevoked,
      this.checkExpire,
    ]
      .map(value => (value ? 'X' : 'O'))
      .join('');

    switch (mappedFilters) {
      // ALL ENABLED
      case 'XXX':
        this.filteredCertificateList = this.searchedCertificateList;
        break;
      // ALL DISABLED
      case 'OOO':
        this.filteredCertificateList = [];
        break;
      // ACTIVE
      case 'XOO':
        this.filteredCertificateList = this.searchedCertificateList.filter(
          data => this.matchValue(data, 'active')
        );
        break;
      // REVOKED
      case 'OXO':
        this.filteredCertificateList = this.searchedCertificateList.filter(
          data =>
            this.matchValue(data, 'revoked') ||
            this.matchValue(data, 'disabled')
        );
        break;
      // EXPIRING / EXPIRED
      case 'OOX':
        this.filteredCertificateList = this.searchedCertificateList.filter(
          data => this.matchValue(data, 'expir')
        );
        break;
      // ACTIVE && REVOKED
      case 'XXO':
        this.filteredCertificateList = this.searchedCertificateList.filter(
          data =>
            this.matchValue(data, 'active') ||
            this.matchValue(data, 'revoked') ||
            this.matchValue(data, 'disabled')
        );
        break;
      // ACTIVE && EXPIRING / EXPIRED
      case 'XOX':
        this.filteredCertificateList = this.searchedCertificateList.filter(
          data =>
            this.matchValue(data, 'active') || this.matchValue(data, 'expir')
        );
        break;
      // REVOKED && EXPIRING / EXPIRED
      case 'OXX':
        this.filteredCertificateList = this.searchedCertificateList.filter(
          data =>
            this.matchValue(data, 'revoked') ||
            this.matchValue(data, 'disabled') ||
            this.matchValue(data, 'expir')
        );
        break;
    }

    this.orderTable(this.listOrder);
    this.currentPage = 1;
  }

  filterDate(startDate: any, endDate: any) {
    // workaround per far funzionare l' isBetween()
    const fromDate = this.formatDate(startDate);
    const toDate = this.formatDate(endDate);

    console.log(
      '[DATE] from: ',
      this.formatDate(startDate),
      'to: ',
      this.formatDate(endDate)
    );

    this.filteredCertificateList = this.searchedCertificateList.filter(data => {
      const date = moment(data.issuanceDate, 'YYYY-MM-DD');
      return date.isBetween(startDate, endDate, undefined, '[]');
    });

    this.orderTable(this.listOrder);
  }

  resetFilters() {
    this.searchedCertificateList = this._certificateList;
    this.filteredCertificateList = this.searchedCertificateList;

    this.checkActive = true;
    this.checkExpire = true;
    this.checkRevoked = true;

    this.startDate.setValue('');
    this.endDate.setValue('');
    this.searchString = '';
    this.orderTable('MOST_RECENT');
  }

  matchValue(data, value) {
    return Object.keys(data)
      .map(key => {
        return new RegExp(value, 'gi').test(data[key]);
      })
      .some(result => result);
  }

  public onPageChanged(data: any): void {
    console.log(data);
    this.currentPage = data.currentPage;
    this.updateTableItems();
  }

  // Gestisce l'ordinamento della tabella ng-ui-kit-table
  onTableSort(event: ISortBy): void {
    const { field, direction } = event;

    // Mappa i campi della tabella ai campi del modello CertificateList
    const fieldMap: { [key: string]: string } = {
      subjectName: 'subjectName',
      issuanceDate: 'issuanceDate',
    };

    const actualField = fieldMap[field];
    if (!actualField) return;

    // Ordina la lista in base al campo e direzione
    this.filteredCertificateList.sort((a, b) => {
      let compareResult = 0;

      if (actualField === 'issuanceDate') {
        compareResult =
          this.getIssuanceDateValue(a.issuanceDate) -
          this.getIssuanceDateValue(b.issuanceDate);
      } else if (actualField === 'subjectName') {
        compareResult = a.subjectName.localeCompare(b.subjectName);
      }

      return direction === 'desc' ? -compareResult : compareResult;
    });

    // Aggiorna gli elementi della tabella e rilevamento cambiamenti
    this.updateTableItems();
    this.changeDetectorRef.detectChanges();
  }

  // Helper per ottenere aria-label per lo status
  getStatusAria(status: string): string {
    const statusMap: { [key: string]: string } = {
      active: 'Certificato attivo',
      expired: 'Certificato scaduto',
      expiring: 'Certificato in scadenza',
      to_activate: 'Certificato da attivare',
      revoked: 'Certificato revocato',
      suspended: 'Certificato sospeso',
    };
    return statusMap[status] || 'Stato sconosciuto';
  }

  options: DropdownItem[] = [
    {
      label: '5',
    },
    {
      label: '10',
    },
    {
      label: '25',
    },
    {
      label: '50',
    },
  ];

  onChangeItemsPerPage(item: DropdownItem) {
    this.options = this.options.map(o => {
      o.selected = o.label === item.label;
      return o;
    });
    this.itemsPerPage = parseInt(this.options.find(o => o.selected).label);
  }

  login(certId: string, profileName: string, status?: string) {
    const selection = window.getSelection();

    if (!selection || selection.toString() === '') {
      this.certificateService.certID = certId;

      this.certificateService.getPromiseCertificate().then(certificate => {
        this.localStorageService.store('certificate', certificate);
        const isSubCAPeru = this.subCAPeruTemplateList.includes(profileName);
        this.localStorageService.store('isSubCAPeru', isSubCAPeru);

        if (
          certificate.certificateStatus === 'REGISTERED' ||
          status === 'to_activate'
        ) {
          this.router.navigate(['/activation-steps']);
        } else {
          this.userService.observeSigns();
          this.cookieService.set('isac-app', 'legalcert_mysign', {
            path: '/',
            domain: '.infocert.it',
            sameSite: 'Strict',
            secure: true,
          });

          if (status.includes('expir')) {
            this.router.navigate(['/renew']);
          } else {
            this.router.navigate(['/homepage']);
          }
        }
      });
    }
  }

  removeTINIT(CF: string): string {
    let response = CF;

    if (CF.includes('TINIT-')) {
      response = CF.slice(6);
    }

    return response;
  }

  formatDate(date: string) {
    if (!date?.trim()) {
      return '';
    }
    const parsed = moment(date, [moment.ISO_8601, 'YYYY-MM-DD', 'DD/MM/YYYY'], true);
    return parsed.isValid() ? parsed.format('YYYY-MM-DD') : '';
  }

  private getIssuanceDateValue(date: string): number {
    if (!date?.trim()) {
      return 0;
    }
    const parsed = moment(date, [moment.ISO_8601, 'YYYY-MM-DD', 'DD/MM/YYYY'], true);
    return parsed.isValid() ? parsed.valueOf() : 0;
  }
}
